import "@/index.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./lib/auth";
import { ConfirmProvider } from "@/components/ui/confirm";
import RequireAuth from "./components/RequireAuth";
import RequireAdmin from "./components/RequireAdmin";
import RequireMarketing from "./components/RequireMarketing";
import AppLayout from "./components/AppLayout";
import AdminLayout from "./components/AdminLayout";
import Login from "./pages/Login";
import RequireOwner from "./components/RequireOwner";


// Routes load on demand. Everything used to sit in one bundle that had to be
// downloaded and parsed before anything appeared — the trips page waited on the
// admin panel, the marketing tools and the AI assistant.
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const JoinWorkspace = lazy(() => import("./pages/JoinWorkspace"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Clients = lazy(() => import("./pages/Clients"));
const ClientImports = lazy(() => import("./pages/ClientImports"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Extension = lazy(() => import("./pages/Extension"));
const ClientDetail = lazy(() => import("./pages/ClientDetail"));
const Trips = lazy(() => import("./pages/Trips"));
const TripDetail = lazy(() => import("./pages/TripDetail"));
const Pipeline = lazy(() => import("./pages/Pipeline"));
const Flights = lazy(() => import("./pages/Flights"));
const Places = lazy(() => import("./pages/Places"));
const AiAssistant = lazy(() => import("./pages/AiAssistant"));
const Messaging = lazy(() => import("./pages/Messaging"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Settings = lazy(() => import("./pages/Settings"));
const Marketing = lazy(() => import("./pages/Marketing"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const Segments = lazy(() => import("./pages/Segments"));
const Popups = lazy(() => import("./pages/Popups"));
const Leads = lazy(() => import("./pages/Leads"));
const Calls = lazy(() => import("./pages/Calls"));
const Social = lazy(() => import("./pages/Social"));
const PublicTrip = lazy(() => import("./pages/PublicTrip"));
const ClientIntakeForm = lazy(() => import("./pages/ClientIntakeForm"));
const PaymentConfirmation = lazy(() => import("./pages/PaymentConfirmation"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminAgencies = lazy(() => import("./pages/admin/AdminAgencies"));
const AdminAgencyDetail = lazy(() => import("./pages/admin/AdminAgencyDetail"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminPromos = lazy(() => import("./pages/admin/AdminPromos"));
const AdminApiKeys = lazy(() => import("./pages/admin/AdminApiKeys"));
const AdminInquiries = lazy(() => import("./pages/admin/AdminInquiries"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AgencyAdmin = lazy(() => import("./pages/AgencyAdmin"));
const CcAuthForm = lazy(() => import("./pages/CcAuthForm"));
const NotFound = lazy(() => import("./pages/NotFound"));

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <ConfirmProvider>
        <Toaster
          position="top-right"
          richColors
          closeButton
          expand
          gap={10}
          visibleToasts={5}
          pauseWhenPageIsHidden
          toastOptions={{ duration: 4500 }}
        />
        <Suspense fallback={<div className="min-h-screen bg-[#FAF6EE]" />}>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/extension" element={<Extension />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/join/:token" element={<JoinWorkspace />} />
          <Route path="/trip/:token" element={<PublicTrip />} />
          <Route path="/trip/:token/intake" element={<ClientIntakeForm />} />
          <Route path="/client/:token/intake" element={<ClientIntakeForm />} />
          <Route path="/cc-auth/:token" element={<CcAuthForm />} />
          <Route path="/payment/confirmation" element={<PaymentConfirmation />} />
          <Route path="/app" element={<RequireAuth><AppLayout /></RequireAuth>}>
            <Route index element={<Dashboard />} />
            <Route path="clients" element={<Clients />} />
            <Route path="clients/imports" element={<ClientImports />} />
            <Route path="clients/:id" element={<ClientDetail />} />
            <Route path="trips" element={<Trips />} />
            <Route path="trips/:id" element={<TripDetail />} />
            <Route path="pipeline" element={<Pipeline />} />
            <Route path="flights" element={<Flights />} />
            <Route path="places" element={<Places />} />
            <Route path="ai" element={<AiAssistant />} />
            <Route path="messaging" element={<Messaging />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="marketing" element={
              <RequireMarketing><Marketing /></RequireMarketing>
            } />
            <Route path="marketing/campaigns" element={
              <RequireMarketing feature="Campaigns"><Campaigns /></RequireMarketing>
            } />
            <Route path="marketing/segments" element={
              <RequireMarketing feature="Audiences"><Segments /></RequireMarketing>
            } />
            <Route path="marketing/popups" element={
              <RequireMarketing feature="Lead forms"><Popups /></RequireMarketing>
            } />
            <Route path="marketing/leads" element={
              <RequireMarketing feature="Leads"><Leads /></RequireMarketing>
            } />
            <Route path="marketing/calls" element={
              <RequireMarketing feature="Calls"><Calls /></RequireMarketing>
            } />
            <Route path="marketing/social" element={
              <RequireMarketing feature="Social scheduler"><Social /></RequireMarketing>
            } />
            <Route path="settings" element={<Settings />} />
            <Route path="agency-admin" element={<RequireOwner><AgencyAdmin /></RequireOwner>} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Admin panel — dark theme, separate layout */}
          <Route path="/admin" element={<RequireAuth><RequireAdmin><AdminLayout /></RequireAdmin></RequireAuth>}>
            <Route index element={<AdminDashboard />} />
            <Route path="agencies" element={<AdminAgencies />} />
            <Route path="agencies/:ws_id" element={<AdminAgencyDetail />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:user_id" element={<AdminUserDetail />} />
            <Route path="promos" element={<AdminPromos />} />
            <Route path="api-keys" element={<AdminApiKeys />} />
            <Route path="inquiries" element={<AdminInquiries />} />
            <Route path="inquiries/:inquiry_id" element={<AdminInquiries />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="blog/new" element={<AdminBlogEditor />} />
            <Route path="blog/:post_id" element={<AdminBlogEditor />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Catch-all — any unmatched path shows the 404 page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </ConfirmProvider>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
